import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, BookOpen, CheckCircle2, Circle, AlignLeft, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Rich } from "@/components/notes/Rich";
import { NoteBlock, SUMMARY_TYPES } from "@/components/notes/NoteBlocks";
import { NotesToc, NotesTocMobile } from "@/components/notes/NotesToc";
import { DownloadNotesPdf } from "@/components/notes/DownloadNotesPdf";

const ModeToggle = ({ summary, setSummary }) => (
  <div className="inline-flex rounded-full border border-white/10 bg-[#152040] p-1" data-testid="notes-mode-toggle">
    {[[false, "Detailed", Layers], [true, "Summary", AlignLeft]].map(([val, label, Icon]) => (
      <button key={label} onClick={() => setSummary(val)}
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${summary === val ? "bg-yellow-300 text-slate-900" : "text-zinc-400 hover:text-zinc-100"}`}
        data-testid={`notes-mode-${label.toLowerCase()}`}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    ))}
  </div>
);

const ReadButton = ({ isRead, onToggle, topicId }) => (
  <button onClick={onToggle}
    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${isRead ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-200" : "border-white/15 text-zinc-300 hover:border-emerald-400/50 hover:text-emerald-200"}`}
    data-testid={`mark-read-${topicId}`}>
    {isRead ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
    {isRead ? "Marked as read" : "Mark as read"}
  </button>
);

const TopicSection = ({ topic, index, chapterId, summary, isRead, onToggleRead }) => {
  const blocks = summary ? topic.blocks.filter((b) => SUMMARY_TYPES.has(b.type)) : topic.blocks;
  return (
    <section id={`topic-${topic.id}`} data-topic-id={topic.id} className="scroll-mt-28 pb-14 mb-14 border-b border-white/5 last:border-0" data-testid={`notes-topic-${topic.id}`}>
      <div className="font-mono text-xs text-pink-300 mb-1">{String(index).padStart(2, "0")}</div>
      <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-50 mb-2"><Rich>{topic.title}</Rich></h2>
      <p className="text-zinc-400 text-sm mb-6"><Rich>{topic.summary}</Rich></p>
      <div className="space-y-6">
        {blocks.map((b) => <NoteBlock key={b.id} block={b} chapterId={chapterId} summary={summary} />)}
        {summary && blocks.length === 0 && <p className="text-xs text-zinc-500 italic">Switch to Detailed to read this topic's explanations and examples.</p>}
      </div>
      <div className="mt-8"><ReadButton isRead={isRead} onToggle={onToggleRead} topicId={topic.id} /></div>
    </section>
  );
};

function useActiveTopic(topics) {
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (!topics.length) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.dataset.topicId)),
      { rootMargin: "-20% 0px -70% 0px" }
    );
    document.querySelectorAll("[data-topic-id]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [topics]);
  return active;
}

export default function ChapterNotes() {
  const { chapterId } = useParams();
  const { user } = useAuth();
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState(null);
  const [read, setRead] = useState([]);
  const [summary, setSummary] = useState(false);
  const topics = notes?.topics || [];
  const active = useActiveTopic(topics);

  useEffect(() => {
    api.get(`/notes/${chapterId}`).then((r) => setNotes(r.data)).catch((e) => setError(e.response?.data?.detail || "Couldn't load notes"));
  }, [chapterId]);

  useEffect(() => {
    if (user) api.get(`/notes/${chapterId}/progress`).then((r) => setRead(r.data.read_topics));
  }, [user, chapterId]);

  const jump = useCallback((id) => document.getElementById(`topic-${id}`)?.scrollIntoView({ behavior: "smooth" }), []);

  const toggleRead = async (topicId) => {
    if (!user) return toast.error("Log in to track your reading progress");
    const r = await api.put(`/notes/${chapterId}/progress`, { topic_id: topicId, read: !read.includes(topicId) });
    setRead(r.data.read_topics);
  };

  if (error) return <div className="text-zinc-400" data-testid="notes-error">{error}</div>;
  if (!notes) return <div className="text-zinc-500" data-testid="notes-loading">Loading notes…</div>;

  const pct = Math.round((read.length / topics.length) * 100);

  return (
    <div className="max-w-6xl mx-auto fade-up" data-testid="chapter-notes-page">
      <Link to={`/chapters/${chapterId}`} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-100 mb-5" data-testid="notes-back-link">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to chapter
      </Link>
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2 inline-flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" /> Class {notes.class_level} · Chapter notes
          {notes.status === "needs_review" && <span className="normal-case tracking-normal rounded-full border border-amber-300/40 text-amber-200 px-2 py-0.5 text-[10px]" data-testid="notes-review-badge">Draft · under review</span>}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-50 mb-5" data-testid="notes-title">{notes.chapter_title}</h1>
        <div className="flex flex-wrap items-center gap-4">
          <ModeToggle summary={summary} setSummary={setSummary} />
          <DownloadNotesPdf notes={notes} />
          <DownloadNotesPdf notes={notes} variant="sheet" />
          <div className="flex items-center gap-3 text-xs text-zinc-400" data-testid="notes-progress">
            <div className="w-32 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-emerald-400 transition-[width] duration-500" style={{ width: `${user ? pct : 0}%` }} /></div>
            {user ? `${read.length}/${topics.length} topics read` : "Log in to track progress"}
          </div>
        </div>
      </header>
      <NotesTocMobile topics={topics} active={active} read={read} onJump={jump} />
      <div className="grid lg:grid-cols-[240px_1fr] gap-10 mt-4">
        <aside className="hidden lg:block"><div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto"><NotesToc topics={topics} active={active} read={read} onJump={jump} /></div></aside>
        <article className="min-w-0 max-w-3xl">
          {topics.map((t, i) => (
            <TopicSection key={t.id} topic={t} index={i} chapterId={chapterId} summary={summary} isRead={read.includes(t.id)} onToggleRead={() => toggleRead(t.id)} />
          ))}
        </article>
      </div>
    </div>
  );
}
